import React, { useEffect } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { Select } from './ui';

export interface OrganizationSelectorProps {
  value: string;
  onChange: (orgId: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  autoSelectSingle?: boolean;  // Auto-select if only one org (default: true)
  autoSelectCurrent?: boolean; // Auto-select current org if available (default: true)
  className?: string;
}

/**
 * Reusable organization dropdown with auto-select logic.
 *
 * Auto-selection priority:
 * 1. If autoSelectCurrent=true and user has a current org, select it
 * 2. If autoSelectSingle=true and user has only one org, select it
 *
 * @example
 * <OrganizationSelector
 *   value={selectedOrgId}
 *   onChange={setSelectedOrgId}
 *   label="Share with Organization"
 *   required
 * />
 */
export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  value,
  onChange,
  label = 'Organization',
  required = false,
  disabled = false,
  autoSelectSingle = true,
  autoSelectCurrent = true,
  className = '',
}) => {
  const { organizations, organization: currentOrg } = useOrganization();

  // Auto-select logic
  useEffect(() => {
    // Skip if already selected or disabled
    if (value || disabled || organizations.length === 0) return;

    // Priority 1: Auto-select current org if requested
    if (autoSelectCurrent && currentOrg) {
      onChange(currentOrg.id);
      return;
    }

    // Priority 2: Auto-select if only one org
    if (autoSelectSingle && organizations.length === 1) {
      onChange(organizations[0].id);
    }
  }, [organizations, currentOrg, value, onChange, disabled, autoSelectSingle, autoSelectCurrent]);

  // Don't render if no organizations
  if (organizations.length === 0) {
    return null;
  }

  // Don't render dropdown if only one org and it's auto-selected
  if (autoSelectSingle && organizations.length === 1 && value) {
    return (
      <div className={className}>
        <label className="text-sm font-medium text-text-secondary">{label}</label>
        <div className="px-3 py-2 rounded-lg border border-border-light bg-bg-secondary text-text-primary text-sm" style={{ cursor: 'not-allowed' }}>
          {organizations[0].name}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        size="md"
        label={`${label}${required ? ' *' : ''}`}
      >
        <option value="">Select organization...</option>
        {organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </Select>
    </div>
  );
};
